import {MxSelector, MxSelectorCreator, NO_NAME_PREFIX} from '@aviary/store/store.types';
import {createFeatureSelector, createSelector} from '@ngrx/store';
import {HoldsState} from '@store/holds/holds.state';
import {DISPLAYABLE_HOLD_STATUSES, Hold, HOLDS_FEATURE_KEY, ReadyForPickupGroup, ReadyForPickupHolds} from '@store/holds/holds.types';
import {
  generateIndependentSelectorCreatorsFor,
  generateLoadStatusSelectorsFor,
  generateSelectorCreatorsDependentOn
} from '@aviary/store/store.selector-generators';
import {FALLBACK_HOLD} from '@store/holds/holds.fallback';
import {MinimalCatalogRecord} from '@lib/components/combined-cover-title-detail/combined-cover-title-detail.component';
import {FALLBACK_MATERIAL_TYPE} from '@aviary/store/common/common.fallback';
import {isArray, isDefined, isPopulatedArray, isPositiveNumber, isUndefined} from '@aviary/store/common/typing.helpers';
import {DateOnlyIsoString} from '@aviary/store/common/common.types';
import {CatalogRecordOrID} from '@store/catalog/catalog.types';
import {BorrowingHistoryRecord} from '@store/checkouts/checkouts.types';

export namespace HoldsSelectors {
  export const entireState: MxSelector<HoldsState> = createFeatureSelector(HOLDS_FEATURE_KEY);

  export namespace Stateless {
    export const {
      catalogRecordIdFor,
      catalogRecordIdentifierFor,
      coverImageUrlFor,
      itemTitleFor,
      materialTypeFor,
      statusFor,
      queueIdxFor,
      circulatingCopiesFor,
      pickupBranchNameFor,
      expireDateFor,
      notWantedBeforeFor
    } = generateIndependentSelectorCreatorsFor(FALLBACK_HOLD, NO_NAME_PREFIX);

    export const minimalCatalogRecordFor: MxSelectorCreator<[Hold], MinimalCatalogRecord> =
      hold => createSelector(
        catalogRecordIdFor(hold),
        catalogRecordIdentifierFor(hold),
        coverImageUrlFor(hold),
        itemTitleFor(hold),
        (id, identifier, imageUrl, title): MinimalCatalogRecord => ({id, identifier, imageUrl, title})
      );

    export const queueIndexAsStringFor: MxSelectorCreator<[Hold], string> =
      hold => createSelector(
        queueIdxFor(hold),
        String
      );

    export const {
      materialTypeDescriptionFor
    } = generateSelectorCreatorsDependentOn(materialTypeFor, FALLBACK_MATERIAL_TYPE, 'materialType');

    export const isEditable: MxSelectorCreator<[Hold], boolean> =
      hold => createSelector(
        statusFor(hold),
        (status) => status === 'REQUESTED' || status === 'PAUSED'
      );

    export const isRequested: MxSelectorCreator<[Hold], boolean> =
      hold => createSelector(
        statusFor(hold),
        (status) => status === 'REQUESTED'
      );

    export const displayableHoldStatus: MxSelectorCreator<[Hold], string> =
      hold => createSelector(
        statusFor(hold),
        (status) => DISPLAYABLE_HOLD_STATUSES.get(status) ?? ''
      );

    export const cannotEditHoldTooltip: MxSelectorCreator<[Hold], string> =
      hold => createSelector(
        displayableHoldStatus(hold),
        (displayableStatus) => `Hold with ${displayableStatus} status cannot be edited.`
      );
  } // namespace Stateless

  export namespace ReadyForPickup {
    const substate: MxSelector<HoldsState['readyForPickup']> = createSelector(
      entireState,
      ({readyForPickup}) => readyForPickup
    );

    export const {
      loading,
      awaitingFirstLoad,
      awaitingAnyLoad,
      loaded,
    } = generateLoadStatusSelectorsFor(substate);

    export const readyForPickup: MxSelector<ReadyForPickupHolds> = createSelector(
      substate,
      ({data}) => data ?? {}
    );

    export const earliestPickupExpireDate: MxSelector<DateOnlyIsoString | undefined> = createSelector(
      readyForPickup,
      _readyForPickup => {
        function earlierOf(earliestDateSoFar: DateOnlyIsoString | undefined, candidateDate: DateOnlyIsoString): DateOnlyIsoString {
          return isUndefined(earliestDateSoFar) || candidateDate < earliestDateSoFar
            ? candidateDate
            : earliestDateSoFar;
        }

        // _readyForPickup is Record<number, Hold[]>, so flatten to Hold[], map to just the dates we need, and find the earliest or undefined if no dates exist.
        return Object.values(_readyForPickup)
          .flat()
          .map(hold => hold?.pickupExpireDate)
          .filter(isDefined)
          .reduce(earlierOf, undefined);
      }
    );

    export const groups: MxSelector<ReadyForPickupGroup[]> = createSelector(
      readyForPickup,
      _readyForPickup =>
        pickupsByGroups(_readyForPickup)
    );

    export const totalCount: MxSelector<number> = createSelector(
      groups,
      groups =>
        groups.reduce(
          (accumulator, group) => accumulator + group.holds.length,
          0
        )
    );

    export const hasItemsReadyForPickup: MxSelector<boolean> = createSelector(
      totalCount,
      isPositiveNumber
    );
  } // namespace ReadyForPickup

  export namespace OnHold {
    const substate: MxSelector<HoldsState['onHold']> = createSelector(
      entireState,
      ({onHold}) => onHold
    );

    export const {
      loading,
      awaitingFirstLoad,
      awaitingAnyLoad,
      loaded,
    } = generateLoadStatusSelectorsFor(substate);

    export const holds: MxSelector<Hold[]> = createSelector(
      substate,
      ({data}) => data
    );

    export const hasHolds: MxSelector<boolean> = createSelector(
      holds,
      isPopulatedArray
    );

    export const {
      minimalCatalogRecordFor,
      materialTypeDescriptionFor,
      statusFor,
      queueIndexAsStringFor,
      circulatingCopiesFor,
      pickupBranchNameFor,
      notWantedBeforeFor
    } = Stateless;
  } // namespace OnHold

  export namespace HoldsForCatalogRecords {
    const substate: MxSelector<HoldsState['holdsForCatalogRecords']> = createSelector(
      entireState,
      ({holdsForCatalogRecords}) => holdsForCatalogRecords
    );

    export const {
      loading,
      awaitingFirstLoad,
      awaitingAnyLoad,
      loaded,
      finishedLoading
    } = generateLoadStatusSelectorsFor(substate);

    const holds: MxSelector<Hold[]> = createSelector(
      substate,
      ({data}) => data ?? []
    );

    const holdsForCatalogRecord: MxSelectorCreator<[CatalogRecordOrID], Hold[]> =
      recordOrId => createSelector(
        holds,
        (loadedHolds) => loadedHolds.filter((hold) => hold.catalogRecordId === getCatalogRecordID(recordOrId))
      );

    export const hasHolds: MxSelectorCreator<[CatalogRecordOrID], boolean> =
      recordOrId => createSelector(
        holdsForCatalogRecord(recordOrId),
        isPopulatedArray
      );

    export const hasOneHold: MxSelectorCreator<[CatalogRecordOrID], boolean> =
      recordOrId => createSelector(
        holdsForCatalogRecord(recordOrId),
        (holds) => isArray(holds) && holds.length === 1
      );

    export const singleHold: MxSelectorCreator<[CatalogRecordOrID], Hold> =
      recordOrId => createSelector(
        holdsForCatalogRecord(recordOrId),
        (loadedHolds) => isPopulatedArray(loadedHolds) ? loadedHolds[0] : FALLBACK_HOLD
      );

    export const singleHoldIsEditable: MxSelectorCreator<[CatalogRecordOrID], boolean> =
      recordOrId => createSelector(
        singleHold(recordOrId),
        (hold) => hold.status === 'REQUESTED' || hold.status === 'PAUSED'
      );

    export const editHoldTooltip: MxSelectorCreator<[CatalogRecordOrID], string> =
      recordOrId => createSelector(
        singleHold(recordOrId),
        singleHoldIsEditable(recordOrId),
        (hold, editable) => {
          // Hold is editable, so reflect that
          if (editable) {
            return 'Edit Hold';
          }

          // Hold is not editable, create a tooltip with a displayable status.
          const displayableStatus: string = DISPLAYABLE_HOLD_STATUSES.get(hold.status) ?? 'this';
          return `Hold with ${displayableStatus} status cannot be edited.`
        }
      );


    // //////////////////////////////////////////////////////////////////////////////////////////////
    // The following selectors are used for the display/funcionality of the hold icons on list items.
    // //////////////////////////////////////////////////////////////////////////////////////////////

    // Single Hold
    export const showEditHoldIconFor: MxSelectorCreator<[number], boolean> =
      (catalogRecordId) => createSelector(
        hasHolds(catalogRecordId),
        hasOneHold(catalogRecordId),
        (hasAnyHolds, hasOnlyOneHold) => hasAnyHolds && hasOnlyOneHold
      );

    export const hideEditHoldIconFor: MxSelectorCreator<[number], boolean> =
      (catalogRecordId) => createSelector(
        showEditHoldIconFor(catalogRecordId),
        (show) => !show
      );

    // Multiple Holds
    export const showViewHoldsIconFor: MxSelectorCreator<[number], boolean> =
      (catalogRecordId) => createSelector(
        hasHolds(catalogRecordId),
        hasOneHold(catalogRecordId),
        (hasAnyHolds, hasOnlyOneHold) => hasAnyHolds && !hasOnlyOneHold
      );

    export const hideViewHoldsIconFor: MxSelectorCreator<[number], boolean> =
      (catalogRecordId) => createSelector(
        showViewHoldsIconFor(catalogRecordId),
        (show) => !show
      );

    // No Holds
    export const showAddHoldIconFor: MxSelectorCreator<[number], boolean> =
      (catalogRecordId) => createSelector(
        hasHolds(catalogRecordId),
        (hasAnyHolds) => !hasAnyHolds
      );

    export const hideAddHoldIconFor: MxSelectorCreator<[number], boolean> =
      (catalogRecordId) => createSelector(
        showAddHoldIconFor(catalogRecordId),
        (show) => !show
      );

    export const placeholderEnabled: MxSelectorCreator<[BorrowingHistoryRecord], boolean> =
      () => createSelector(
        () => false
      );
  } // namespace HoldsForCatalogRecords

  export namespace DependentReadyForPickup {
    const substate: MxSelector<HoldsState['dependentReadyForPickup']> = createSelector(
      entireState,
      ({dependentReadyForPickup}) => dependentReadyForPickup
    );

    export const {
      loading,
      awaitingFirstLoad,
      awaitingAnyLoad,
      loaded,
    } = generateLoadStatusSelectorsFor(substate);

    export const readyForPickup: MxSelector<ReadyForPickupHolds> = createSelector(
      substate,
      ({data}) => data ?? {}
    );

    export const groups: MxSelector<ReadyForPickupGroup[]> = createSelector(
      readyForPickup,
      _readyForPickup =>
        pickupsByGroups(_readyForPickup)
    );

    export const totalCount: MxSelector<number> = createSelector(
      groups,
      groups =>
        groups.reduce(
          (accumulator, group) => accumulator + group.holds.length,
          0
        )
    );

    export const hasItemsReadyForPickup: MxSelector<boolean> = createSelector(
      totalCount,
      isPositiveNumber
    );
  } // namespace DependentReadyForPickup

  export namespace DependentOnHold {
    const substate: MxSelector<HoldsState['dependentOnHold']> = createSelector(
      entireState,
      ({dependentOnHold}) => dependentOnHold
    );

    export const {
      loading,
      awaitingFirstLoad,
      awaitingAnyLoad,
      loaded,
    } = generateLoadStatusSelectorsFor(substate);

    export const holds: MxSelector<Hold[]> = createSelector(
      substate,
      ({data}) => data
    );

    export const hasHolds: MxSelector<boolean> = createSelector(
      holds,
      isPopulatedArray
    );

    export const {
      minimalCatalogRecordFor,
      materialTypeDescriptionFor,
      statusFor,
      queueIndexAsStringFor,
      circulatingCopiesFor,
      pickupBranchNameFor,
      notWantedBeforeFor
    } = Stateless;
  } // namespace DependentOnHold
}

function getCatalogRecordID(recordOrID: CatalogRecordOrID): number {
  return isPositiveNumber(recordOrID) ? recordOrID : recordOrID.id;
}

function pickupsByGroups(readyForPickup: ReadyForPickupHolds): ReadyForPickupGroup[] {
  return Object.entries(readyForPickup).reduce<ReadyForPickupGroup[]>(
    (accumulator, [branchId, holds]) => {
      const holdsCount: number = holds?.length ?? 0;

      if (holdsCount > 0) {
        accumulator.push({
          branchId: Number(branchId),
          holds,
          itemCount: `${holdsCount} item${holdsCount === 1 ? '' : 's'}`
        })
      }

      return accumulator;
    },
    []
  );
}
